import React, { useState } from "react";
import { promptRunsApi } from "../api/routes";
import { Button, Card, Input, Select, Toast } from "../components/ui/Controls";
import { usePanelCache } from "../state/PanelCacheContext";

function PromptRunsPanel() {
  const [userLookup, setUserLookup] = useState("");
  const [traceId, setTraceId] = useState("");
  const [promptType, setPromptType] = useState("");
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [limit, setLimit] = useState("100");
  const [toast, setToast] = useState(null);
  const [searching, setSearching] = useState(false);
  const [panelState, setPanelState] = usePanelCache("promptRuns");
  const results = panelState.results || [];
  const showSearch = panelState.showSearch ?? true;
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("list"); // list | detail

  const fetchRuns = async () => {
    if (!userLookup) return;
    setSearching(true);
    try {
      const res = await promptRunsApi.byUser(userLookup, {
        limit,
        ...(traceId ? { traceId } : {}),
        ...(promptType ? { promptType } : {}),
        ...(status ? { status } : {}),
        ...(fromDate ? { from: fromDate } : {}),
        ...(toDate ? { to: toDate } : {}),
      });
      const list = Array.isArray(res.data) ? res.data : [];
      setPanelState({ results: list, showSearch: false });
      setSelected(list[0] || null);
      setMode(list.length ? "detail" : "list");
    } catch (e) {
      setToast({ message: e.message || "Error fetching prompt runs", tone: "error" });
      setPanelState({ results: [] });
      setSelected(null);
      setMode("list");
    } finally {
      setSearching(false);
    }
  };

  const renderDetails = (run) => {
    if (!run) return <div style={{ color: "var(--stone-700)" }}>Select a prompt run to view details.</div>;

    const prettyJson = (val) => {
      try {
        return JSON.stringify(JSON.parse(val), null, 2);
      } catch (_) {
        return val || "n/a";
      }
    };

    return (
      <div style={{ display: "grid", gap: 12, overflowX: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
          <div><div style={{ fontWeight: 700 }}>Prompt Type</div><div>{run.promptType}</div></div>
          <div><div style={{ fontWeight: 700 }}>Status</div><div style={{ color: run.status === "SUCCESS" ? "#0c9a4b" : "#c0392b", fontWeight: 700 }}>{run.status}</div></div>
          <div><div style={{ fontWeight: 700 }}>Created</div><div style={{ fontFamily: "monospace" }}>{run.createdAt}</div></div>
          <div><div style={{ fontWeight: 700 }}>Dry Run</div><div>{run.dryRun ? "Yes" : "No"}</div></div>
          <div><div style={{ fontWeight: 700 }}>User ID</div><div style={{ fontFamily: "monospace", fontSize: 12 }}>{run.userId}</div></div>
          <div><div style={{ fontWeight: 700 }}>Trace ID</div><div style={{ fontFamily: "monospace", fontSize: 12 }}>{run.traceId || "n/a"}</div></div>
        </div>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Prompt Messages</div>
          <pre style={{ margin: 0, background: "#0f172a", color: "#e2fbe8", padding: 12, borderRadius: 10, fontSize: 12, maxHeight: 240, overflow: "auto" }}>
            {prettyJson(run.promptMessages)}
          </pre>
        </div>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Raw Response</div>
          <pre style={{ margin: 0, background: "#0f172a", color: "#e2fbe8", padding: 12, borderRadius: 10, fontSize: 12, maxHeight: 240, overflow: "auto" }}>
            {prettyJson(run.rawResponse)}
          </pre>
        </div>
        {run.errorMessage && (
          <div>
            <div style={{ fontWeight: 700, marginBottom: 6, color: "#c0392b" }}>Error Message</div>
            <div style={{ color: "#c0392b" }}>{run.errorMessage}</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {mode === "detail" && selected ? (
        <Card
          title="Prompt Run Details"
          action={<Button tone="ghost" onClick={() => setMode("list")}>← Back to search</Button>}
          tone="accent"
        >
          {renderDetails(selected)}
        </Card>
      ) : (
        <Card
          title="Prompt Runs"
          action={
            <div style={{ display: "flex", gap: 8 }}>
              <Button tone="ghost" onClick={() => setPanelState({ showSearch: !showSearch })}>
                {showSearch ? "Hide search" : "Show search"}
              </Button>
              <Button tone="ghost" onClick={() => { setPanelState({ results: [] }); setSelected(null); }}>Clear</Button>
            </div>
          }
        >
          {showSearch && (
            <Card title="Search by user">
              <Input label="User ID" value={userLookup} onChange={setUserLookup} />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
                <Input label="Trace ID" value={traceId} onChange={setTraceId} placeholder="Optional" />
                <Input label="Limit" type="number" value={limit} onChange={setLimit} />
                <Input label="From" type="date" value={fromDate} onChange={setFromDate} />
                <Input label="To" type="date" value={toDate} onChange={setToDate} />
                <Select label="Prompt type" value={promptType} onChange={setPromptType} options={["", "SMS_INBOUND", "WORKOUT_PLAN_ADAPTATION", "QUESTION_ANSWERING", "SMS_JOBS_DONE", "COMPLETED_WORKOUT_UPLOAD", "ATHLETE_PROFILE_UPDATE", "ASK_QUESTION", "FEEDBACK_CAPTURE", "CONVERSATION_ENDED"]} />
                <Select label="Status" value={status} onChange={setStatus} options={["", "SUCCESS", "ERROR"]} />
              </div>
              <Button onClick={fetchRuns} disabled={!userLookup} loading={searching}>Search</Button>
            </Card>
          )}
          {results.length === 0 ? (
            <div style={{ padding: 12, color: "var(--stone-700)" }}>No prompt runs yet. Search by user.</div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {results.map((run) => (
                <div
                  key={`${run.userId}-${run.createdAt}`}
                  style={{
                    padding: 12,
                    borderRadius: 12,
                    border: "1px solid rgba(15,23,42,0.08)",
                    background: "rgba(15,23,42,0.02)",
                    cursor: "pointer",
                  }}
                  onClick={() => { setSelected(run); setMode("detail"); }}
                >
                  <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1.2fr 0.6fr", gap: 10, alignItems: "center" }}>
                    <div style={{ fontWeight: 700 }}>{run.promptType}</div>
                    <div style={{ color: run.status === "SUCCESS" ? "#0c9a4b" : "#c0392b", fontWeight: 700 }}>{run.status}</div>
                    <div style={{ fontFamily: "monospace", fontSize: 12 }}>{run.createdAt}</div>
                    <div style={{ fontSize: 12 }}>{run.dryRun ? "Yes" : "No"}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </>
  );
}

export default PromptRunsPanel;
